import React, { useCallback, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
  FieldArray,
  FieldGroup,
  FormArray,
  FormBuilder,
  Validators,
} from "react-reactive-form";
import DashboardContent from "../../components/DashboardContent";
import useCsr from "../../hooks/useCsr";
import RedirectHandler from "../../components/RedirectHandler";
import QuestionItem from "../../components/QuestionItem";
import QuestionsAnswersArray from "../../components/QuestionsAnswersArray";
import { CsrAPI } from "../../apis/CsrAPI";
import { ReactComponent as PlusIcon } from "../../assets/images/icons/plus.svg";
import { ReactComponent as BinIcon } from "../../assets/images/icons/bin.svg";
import { CButton } from "@coreui/react";
import Loader from "../../utils/Loader";
import { useSelector } from "react-redux";

const PLACEHOLDER_ANALYTES =
  "Enter the primary analyte or clinical endpoint shown in the table.";
const PLACEHOLDER_TIMEPOINTS =
  "Specify one or more time points exactly as represented in the table.";
const DUPLICATE_BLOCKS_MSG =
  "Two or more analysis blocks have the same values. Duplicate entries are not allowed. Please make them distinct before submitting.";
const DUPLICATE_TIMEPOINTS_MSG =
  "Duplicate timepoints are not allowed. Please enter each timepoint only once.";
const DUPLICATE_ANALYTES_MSG =
  "Duplicate analytes are not allowed. Please enter each analyte only once.";
const DUPLICATE_TREATMENTS_MSG =
  "Duplicate treatments are not allowed. Please enter each treatment only once.";
const EMPTY_QUOTES_MSG =
  "Empty quoted values are not allowed. Please enter a valid value.";

const MAX_BLOCKS = 3;
const GLOBAL_ID = "table-analysis";

function isEmptyQuotesValue(val) {
  const trimmed = (val ?? "").toString().trim();
  return trimmed === "''" || trimmed === '""';
}

function createTreatmentsArray(isComparative, initialData) {
  const arr = new FormArray([]);
  if (isComparative) {
    const items = Array.isArray(initialData)
      ? initialData
      : initialData?.length
        ? [initialData]
        : [{ Treatment: "", Comparator: "" }];
    const list = items.map(t =>
      typeof t === "object" && t !== null && "Treatment" in t
        ? { Treatment: t.Treatment ?? "", Comparator: t.Comparator ?? "" }
        : { Treatment: "", Comparator: "" }
    );
    if (list.length === 0) list.push({ Treatment: "", Comparator: "" });
    list.forEach(item =>
      arr.push(
        FormBuilder.group(
          {
            Treatment: [item.Treatment, Validators.required],
            Comparator: [item.Comparator, Validators.required],
          },
          { updateOn: "change" }
        )
      )
    );
  } else {
    const items = Array.isArray(initialData)
      ? initialData.map(v =>
          typeof v === "string" ? v : (v?.valueHelper ?? "")
        )
      : initialData?.valueHelper != null
        ? [initialData.valueHelper]
        : [""];
    if (items.length === 0) items.push("");
    items.forEach(v =>
      arr.push(
        FormBuilder.group(
          { valueHelper: [v, Validators.required] },
          { updateOn: "change" }
        )
      )
    );
  }
  return arr;
}

function createBlock(blockData) {
  const qVal = blockData?.qAsComparative ?? "comparative";
  const isComparative = qVal === "comparative";
  const qAsComparativeControl = FormBuilder.control(qVal);
  const qAsTreatments = createTreatmentsArray(
    isComparative,
    blockData?.qAsTreatments
  );
  const createAnalyteOrTimepointGroup = v =>
    FormBuilder.group(
      { valueHelper: [typeof v === "string" ? v : ""] },
      { updateOn: "change" }
    );
  const analytesConfig = Array.isArray(blockData?.qAsAnalytes)
    ? blockData.qAsAnalytes
    : [""];
  const qAsAnalytes = FormBuilder.array(
    (analytesConfig.length ? analytesConfig : [""]).map(
      createAnalyteOrTimepointGroup
    ),
    { updateOn: "change" }
  );
  if (qAsAnalytes.length === 0)
    qAsAnalytes.push(createAnalyteOrTimepointGroup(""));
  const timepointsConfig = Array.isArray(blockData?.qAsTimepoints)
    ? blockData.qAsTimepoints
    : [""];
  const qAsTimepoints = FormBuilder.array(
    (timepointsConfig.length ? timepointsConfig : [""]).map(
      createAnalyteOrTimepointGroup
    ),
    { updateOn: "change" }
  );
  if (qAsTimepoints.length === 0)
    qAsTimepoints.push(createAnalyteOrTimepointGroup(""));
  return FormBuilder.group({
    qAsComparative: qAsComparativeControl,
    qAsTreatments,
    qAsAnalytes,
    qAsTimepoints,
  });
}

function buildFormFromConfig(config) {
  const blocks =
    Array.isArray(config) && config.length > 0
      ? config.map(b => createBlock(b))
      : [createBlock(null)];
  const asQuestionsArr = new FormArray(blocks);
  return FormBuilder.group({ asQuestions: asQuestionsArr });
}

function normalizeBlockTreatments(block, isComparative) {
  const arr = block.get("qAsTreatments");
  if (!(arr instanceof FormArray)) return;
  const current = arr.value || [];
  while (arr.length) arr.removeAt(0);
  if (isComparative) {
    const isFromNonComparative = current.some(
      item => item != null && "valueHelper" in item
    );
    const items = isFromNonComparative
      ? [{ Treatment: "", Comparator: "" }]
      : current.map(item => ({
          Treatment: item?.Treatment ?? "",
          Comparator: item?.Comparator ?? "",
        }));
    if (items.length === 0) items.push({ Treatment: "", Comparator: "" });
    const baseKey = Date.now();
    items.forEach((item, idx) => {
      const group = FormBuilder.group({
        Treatment: [item.Treatment, Validators.required],
        Comparator: [item.Comparator, Validators.required],
      });
      group.meta = { key: `qAsTreatments-comp-${baseKey}-${idx}` };
      arr.push(group);
    });
  } else {
    const isFromComparative = current.some(
      item => item != null && ("Treatment" in item || "Comparator" in item)
    );
    const items = isFromComparative
      ? [""]
      : current.map(item => item?.valueHelper ?? "");
    if (items.length === 0) items.push("");
    items.forEach((v, idx) => {
      const group = FormBuilder.group(
        { valueHelper: [v, Validators.required] },
        { updateOn: "change" }
      );
      group.meta = { key: `qAsTreatments-noncomp-${Date.now()}-${idx}` };
      arr.push(group);
    });
  }
}

const TableAnalysisQuestions = () => {
  const { studyId, tableId } = useParams();
  const { state: locationState } = useLocation();
  // const tableName = locationState?.tableName ?? "";
  const selectedTableNameFromStore = useSelector(
    state => state.selectedTable.selectedTableName
  );
  const [section, setSection] = useState(locationState?.section ?? "");
  const [subsection, setSubsection] = useState(locationState?.subsection ?? "");
  const { wrongEnvironment } = useCsr();
  const [rootForm, setRootForm] = useState(() => buildFormFromConfig(null));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState("");
  const [timepointsErrorBlocks, setTimepointsErrorBlocks] = useState([]);
  const [analytesErrorBlocks, setAnalytesErrorBlocks] = useState([]);
  const [treatmentsErrorBlocks, setTreatmentsErrorBlocks] = useState([]);
  const [missingAnalytesBlocks, setMissingAnalytesBlocks] = useState([]);
  const [missingTreatmentsBlocks, setMissingTreatmentsBlocks] = useState([]);
  const [emptyQuotesTreatmentsBlocks, setEmptyQuotesTreatmentsBlocks] =
    useState([]);
  const [emptyQuotesAnalytesBlocks, setEmptyQuotesAnalytesBlocks] = useState(
    []
  );
  const [emptyQuotesTimepointsBlocks, setEmptyQuotesTimepointsBlocks] =
    useState([]);
  const [, setFormTick] = useState(0);

  const asQuestions = rootForm.get("asQuestions");

  useEffect(() => {
    if (locationState?.section != null) setSection(locationState.section);
    if (locationState?.subsection != null)
      setSubsection(locationState.subsection);
  }, [locationState?.section, locationState?.subsection]);

  useEffect(() => {
    if (
      !locationState?.section &&
      !locationState?.subsection &&
      studyId &&
      tableId
    ) {
      let cancelled = false;
      CsrAPI.getAdvSearchDashboard({ studyId })
        .then(data => {
          if (cancelled) return;
          const body = data && typeof data === "object" ? data : {};
          for (const [sectionName, subsections] of Object.entries(body)) {
            if (!Array.isArray(subsections)) continue;
            for (const sub of subsections) {
              const subsectionName = sub?.subsection ?? null;
              const tables = Array.isArray(sub?.tables) ? sub.tables : [];
              const found = tables.find(
                t => t && (t.tableId === tableId || t.table_id === tableId)
              );
              if (found) {
                setSection(sectionName ?? "");
                setSubsection(subsectionName ?? "");
                return;
              }
            }
          }
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }
  }, [studyId, tableId, locationState?.section, locationState?.subsection]);

  useEffect(() => {
    if (!studyId || !tableId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    CsrAPI.getTableConfiguration({ studyId, tableId })
      .then(data => {
        if (cancelled) return;
        try {
          setRootForm(buildFormFromConfig(data));
        } catch {
          setRootForm(buildFormFromConfig(null));
        }
      })
      .catch(() => {
        if (!cancelled) setRootForm(buildFormFromConfig(null));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studyId, tableId]);

  useEffect(() => {
    const sub = rootForm.valueChanges.subscribe(() => setFormTick(t => t + 1));
    return () => sub?.unsubscribe?.();
  }, [rootForm]);

  useEffect(() => {
    if (!asQuestions || !asQuestions.controls) return;
    const subs = asQuestions.controls.map(block => {
      const control = block.get("qAsComparative");
      if (!control) return () => {};
      return control.valueChanges.subscribe(val => {
        const isComparative = val === "comparative";
        setTimeout(() => {
          normalizeBlockTreatments(block, isComparative);
        }, 0);
      });
    });
    return () => subs.forEach(s => s?.unsubscribe?.());
  }, [asQuestions, asQuestions?.length]);

  const addBlock = useCallback(() => {
    if (asQuestions.length >= MAX_BLOCKS) return;
    asQuestions.push(createBlock(null));
    setFormTick(t => t + 1);
  }, [asQuestions]);

  const removeBlock = useCallback(
    index => {
      if (asQuestions.length <= 1) return;
      asQuestions.removeAt(index);
      setFormTick(t => t + 1);
    },
    [asQuestions]
  );

  const getPayload = useCallback(() => {
    const blocksOrValues = asQuestions.controls || [];
    const rawBlocks = blocksOrValues.map(blockControl => {
      const block =
        blockControl &&
        typeof blockControl.value === "object" &&
        blockControl.value !== null
          ? blockControl.value
          : blockControl;
      const qAsComparativeVal = block?.qAsComparative ?? "";
      const isComparative = qAsComparativeVal === "comparative";
      const treatments = (block?.qAsTreatments || []).map(t => {
        if (isComparative)
          return {
            Treatment: (t?.Treatment ?? "").trim(),
            Comparator: (t?.Comparator ?? "").trim(),
          };
        return (t?.valueHelper ?? "").trim();
      });
      const qAsAnalytes = (block?.qAsAnalytes || []).map(a =>
        (a?.valueHelper ?? "").trim()
      );
      const qAsTimepoints = (block?.qAsTimepoints || []).map(t =>
        (t?.valueHelper ?? "").trim()
      );

      const isTreatmentsEmpty =
        treatments.length === 0 ||
        (isComparative
          ? treatments.every(
              t =>
                (t?.Treatment ?? "").trim() === "" &&
                (t?.Comparator ?? "").trim() === ""
            )
          : treatments.every(v => (v ?? "").trim() === ""));
      const isAnalytesEmpty =
        qAsAnalytes.length === 0 ||
        qAsAnalytes.every(a => (a ?? "").trim() === "");
      const isTimepointsEmpty =
        qAsTimepoints.length === 0 ||
        qAsTimepoints.every(t => (t ?? "").trim() === "");

      return {
        qAsComparative: qAsComparativeVal,
        qAsTreatments: isTreatmentsEmpty
          ? isComparative
            ? [{ Treatment: null, Comparator: null }]
            : null
          : treatments,
        qAsAnalytes: isAnalytesEmpty ? null : qAsAnalytes,
        qAsTimepoints: isTimepointsEmpty ? null : qAsTimepoints,
      };
    });
    return {
      table_title: selectedTableNameFromStore,
      section: (section ?? "").trim(),
      subsection: (subsection ?? "").trim(),
      blocks: rawBlocks.map((body, index) => ({
        block_num: index + 1,
        ...body,
      })),
    };
  }, [asQuestions, tableId, section, subsection]);

  const hasDuplicateBlocks = useCallback(() => {
    const payload = getPayload();
    const blocks = payload.blocks || [];
    for (let i = 0; i < blocks.length; i++) {
      for (let j = i + 1; j < blocks.length; j++) {
        const a = { ...blocks[i] };
        const b = { ...blocks[j] };
        delete a.block_num;
        delete b.block_num;
        if (JSON.stringify(a) === JSON.stringify(b)) {
          return true;
        }
      }
    }
    return false;
  }, [getPayload]);

  const runValidation = useCallback(() => {
    const errors = {
      duplicateBlocks: false,
      duplicateTimepoints: [],
      duplicateAnalytes: [],
      duplicateTreatments: [],
      missingAnalytes: [],
      missingTreatments: [],
      emptyQuotesTreatments: [],
      emptyQuotesAnalytes: [],
      emptyQuotesTimepoints: [],
    };
    const blocks = asQuestions?.controls || [];
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      const blockVal = block.value;
      const isComparative =
        (blockVal?.qAsComparative ?? block.get("qAsComparative")?.value) ===
        "comparative";

      const treatments = blockVal?.qAsTreatments || [];
      const hasValidTreatment = (
        Array.isArray(treatments) ? treatments : []
      ).some(t => {
        if (isComparative) {
          const tr = (t?.Treatment ?? "").toString().trim();
          const c = (t?.Comparator ?? "").toString().trim();
          return tr !== "" && c !== "";
        }
        return (t?.valueHelper ?? t ?? "").toString().trim() !== "";
      });
      if (!hasValidTreatment) errors.missingTreatments.push(i);

      if (isComparative) {
        const analytes = blockVal?.qAsAnalytes || [];
        const hasAnalyte = (Array.isArray(analytes) ? analytes : []).some(
          a => (a?.valueHelper ?? a ?? "").toString().trim() !== ""
        );
        if (!hasAnalyte) errors.missingAnalytes.push(i);
      }

      const timepoints = blockVal?.qAsTimepoints || [];
      const tpValues = (Array.isArray(timepoints) ? timepoints : [])
        .map(t => (t?.valueHelper ?? t ?? "").toString().trim().toLowerCase())
        .filter(v => v !== "");
      const tpSeen = new Set();
      let hasTpDup = false;
      for (const v of tpValues) {
        if (tpSeen.has(v)) {
          hasTpDup = true;
          break;
        }
        tpSeen.add(v);
      }
      if (hasTpDup) errors.duplicateTimepoints.push(i);

      const analytes = blockVal?.qAsAnalytes || [];
      const analyteValues = (Array.isArray(analytes) ? analytes : [])
        .map(a => (a?.valueHelper ?? a ?? "").toString().trim().toLowerCase())
        .filter(v => v !== "");
      const analyteSeen = new Set();
      let hasAnalyteDup = false;
      for (const v of analyteValues) {
        if (analyteSeen.has(v)) {
          hasAnalyteDup = true;
          break;
        }
        analyteSeen.add(v);
      }
      if (hasAnalyteDup) errors.duplicateAnalytes.push(i);

      const treatmentValues = (Array.isArray(treatments) ? treatments : [])
        .map(t => {
          if (isComparative) {
            const tr = (t?.Treatment ?? "").toString().trim().toLowerCase();
            const c = (t?.Comparator ?? "").toString().trim().toLowerCase();
            return tr !== "" && c !== "" ? `${tr}|${c}` : "";
          }
          return (t?.valueHelper ?? t ?? "").toString().trim().toLowerCase();
        })
        .filter(v => v !== "");
      const treatmentSeen = new Set();
      let hasTreatmentDup = false;
      for (const v of treatmentValues) {
        if (treatmentSeen.has(v)) {
          hasTreatmentDup = true;
          break;
        }
        treatmentSeen.add(v);
      }
      if (hasTreatmentDup) errors.duplicateTreatments.push(i);

      const hasEmptyQuotesTreatment = (
        Array.isArray(treatments) ? treatments : []
      ).some(t => {
        if (isComparative) {
          return (
            isEmptyQuotesValue(t?.Treatment) ||
            isEmptyQuotesValue(t?.Comparator)
          );
        }
        return isEmptyQuotesValue(t?.valueHelper ?? t);
      });
      if (hasEmptyQuotesTreatment) errors.emptyQuotesTreatments.push(i);

      const hasEmptyQuotesAnalyte = (
        Array.isArray(analytes) ? analytes : []
      ).some(a => isEmptyQuotesValue(a?.valueHelper ?? a));
      if (hasEmptyQuotesAnalyte) errors.emptyQuotesAnalytes.push(i);

      const hasEmptyQuotesTimepoint = (
        Array.isArray(timepoints) ? timepoints : []
      ).some(t => isEmptyQuotesValue(t?.valueHelper ?? t));
      if (hasEmptyQuotesTimepoint) errors.emptyQuotesTimepoints.push(i);
    }
    errors.duplicateBlocks = hasDuplicateBlocks();
    return errors;
  }, [asQuestions, hasDuplicateBlocks]);

  const validateOnBlur = useCallback(() => {
    const validationErrors = runValidation();
    setDuplicateWarning(
      validationErrors.duplicateBlocks ? DUPLICATE_BLOCKS_MSG : ""
    );
    setTimepointsErrorBlocks(validationErrors.duplicateTimepoints || []);
    setAnalytesErrorBlocks(validationErrors.duplicateAnalytes || []);
    setTreatmentsErrorBlocks(validationErrors.duplicateTreatments || []);
    setMissingAnalytesBlocks(validationErrors.missingAnalytes || []);
    setMissingTreatmentsBlocks(validationErrors.missingTreatments || []);
    setEmptyQuotesTreatmentsBlocks(
      validationErrors.emptyQuotesTreatments || []
    );
    setEmptyQuotesAnalytesBlocks(validationErrors.emptyQuotesAnalytes || []);
    setEmptyQuotesTimepointsBlocks(
      validationErrors.emptyQuotesTimepoints || []
    );
  }, [runValidation]);

  const isValidationClean = useCallback(() => {
    const e = runValidation();
    return (
      !e.duplicateBlocks &&
      e.duplicateTimepoints.length === 0 &&
      e.duplicateAnalytes.length === 0 &&
      e.duplicateTreatments.length === 0 &&
      e.missingAnalytes.length === 0 &&
      e.missingTreatments.length === 0 &&
      e.emptyQuotesTreatments.length === 0 &&
      e.emptyQuotesAnalytes.length === 0 &&
      e.emptyQuotesTimepoints.length === 0
    );
  }, [runValidation]);

  useEffect(() => {
    if (!asQuestions?.controls) return;
    const subs = asQuestions.controls.map(block => {
      const control = block.get("qAsComparative");
      if (!control) return () => {};
      return control.valueChanges.subscribe(() => {
        validateOnBlur();
      });
    });
    return () => subs.forEach(s => s?.unsubscribe?.());
  }, [asQuestions, validateOnBlur]);

  const scheduleValidation = useCallback(() => {
    validateOnBlur();
  }, [validateOnBlur]);

  useEffect(() => {
    const sub = rootForm.valueChanges.subscribe(() => scheduleValidation());
    return () => sub?.unsubscribe?.();
  }, [rootForm, scheduleValidation]);

  useEffect(() => {
    const blocks = asQuestions?.controls || [];
    const subs = [];
    blocks.forEach(block => {
      for (const key of ["qAsAnalytes", "qAsTimepoints", "qAsTreatments"]) {
        const arr = block.get(key);
        if (arr?.valueChanges) {
          subs.push(arr.valueChanges.subscribe(() => scheduleValidation()));
        }
      }
    });
    return () => subs.forEach(s => s?.unsubscribe?.());
  }, [asQuestions, scheduleValidation]);

  useEffect(() => {
    if (!loading && asQuestions?.controls?.length) {
      scheduleValidation();
    }
  }, [loading, scheduleValidation, asQuestions?.controls?.length]);

  const markFormTouched = useCallback(() => {
    const blocks = asQuestions?.controls || [];
    blocks.forEach(block => {
      block.markAsTouched?.();
      [
        block.get("qAsTreatments"),
        block.get("qAsAnalytes"),
        block.get("qAsTimepoints"),
      ].forEach(
        arr =>
          arr instanceof FormArray &&
          arr.controls?.forEach(c => c.markAsTouched?.())
      );
    });
  }, [asQuestions]);

  const handleSave = useCallback(
    e => {
      e?.preventDefault?.();
      if (!studyId || !tableId) return undefined;
      markFormTouched();
      const validationErrors = runValidation();
      setDuplicateWarning(
        validationErrors.duplicateBlocks ? DUPLICATE_BLOCKS_MSG : ""
      );
      setTimepointsErrorBlocks(validationErrors.duplicateTimepoints || []);
      setAnalytesErrorBlocks(validationErrors.duplicateAnalytes || []);
      setTreatmentsErrorBlocks(validationErrors.duplicateTreatments || []);
      setMissingAnalytesBlocks(validationErrors.missingAnalytes || []);
      setMissingTreatmentsBlocks(validationErrors.missingTreatments || []);
      setEmptyQuotesTreatmentsBlocks(
        validationErrors.emptyQuotesTreatments || []
      );
      setEmptyQuotesAnalytesBlocks(validationErrors.emptyQuotesAnalytes || []);
      setEmptyQuotesTimepointsBlocks(
        validationErrors.emptyQuotesTimepoints || []
      );
      if (!isValidationClean()) {
        return undefined;
      }
      setSubmitting(true);
      const body = getPayload();
      return CsrAPI.putTableGeneration({ studyId, tableId, body })
        .then(() => {
          toast.success("Data saved successfully!");
          return { success: true };
        })
        .catch(err => {
          if (err?.response?.status === 409) {
            toast.error(
              "Regeneration is already in progress for this table. Please try again later."
            );
            return { success: false };
          }
          if (err?.response?.status === 400) {
            return { success: true };
          }
          if (err?.response?.status === 404) {
            toast.error(
              "Reupload files on file upload page to regenerate the table configuration, then come back to this page to specify analysis criteria and generate the AI response."
            );
            return { success: false };
          }
        })
        .finally(() => setSubmitting(false));
    },
    [
      studyId,
      tableId,
      isValidationClean,
      runValidation,
      markFormTouched,
      getPayload,
    ]
  );

  if (wrongEnvironment) {
    return <RedirectHandler />;
  }

  if (loading) {
    return (
      <DashboardContent
        sidebarCSR={true}
        headerTitle=""
        headerButton={false}
        pageContent={<Loader />}
      />
    );
  }

  const PageContent = () => (
    <>
      <div className="ai-page-title">Specify analysis criteria</div>
      <div className="table-analysis-questions">
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSave(e);
          }}>
          <FieldGroup
            control={rootForm}
            render={() => (
              <FieldArray
                name="asQuestions"
                render={({ controls }) => (
                  <>
                    {controls &&
                      Array.isArray(controls) &&
                      controls.map((blockControl, blockIndex) => (
                        <div
                          key={`block-${blockIndex}`}
                          className="rounded border bg-white shadow-sm pb-0 pt-4 pr-4 pl-4">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <span className="text-muted">
                              {/* Analysis block {blockIndex + 1} */}
                            </span>
                            {controls.length > 1 && (
                              <CButton
                                type="button"
                                color="link"
                                className="text-danger p-0"
                                onClick={() => removeBlock(blockIndex)}>
                                <BinIcon className="me-1" />
                                Remove analysis block
                              </CButton>
                            )}
                          </div>
                          <FieldGroup
                            control={blockControl}
                            render={() => (
                              <>
                                <QuestionItem
                                  questionIndex=""
                                  questionName="qAsComparative"
                                  activator={true}
                                  questionInputType="radio"
                                  questionInputChoice="qAsComparative"
                                  globalId={`${GLOBAL_ID}-block-${blockIndex}`}
                                  required={true}
                                />
                                <QuestionsAnswersArray
                                  questionIndex=""
                                  controlName="qAsTreatments"
                                  questionsName="qAsTreatments"
                                  activator={
                                    blockControl.controls?.qAsComparative
                                      ?.value === "comparative"
                                  }
                                  formGroup={blockControl}
                                  inputTextBoxes={[
                                    ["Treatment", "Treatment", "text"],
                                    ["Comparator", "Comparator", "text"],
                                  ]}
                                  required={true}
                                  onBlur={validateOnBlur}
                                  onChange={scheduleValidation}
                                  error={
                                    treatmentsErrorBlocks.includes(blockIndex)
                                      ? DUPLICATE_TREATMENTS_MSG
                                      : emptyQuotesTreatmentsBlocks.includes(
                                            blockIndex
                                          )
                                        ? EMPTY_QUOTES_MSG
                                        : missingTreatmentsBlocks.includes(
                                              blockIndex
                                            )
                                          ? "At least one treatment comparison is required."
                                          : null
                                  }
                                />
                                <QuestionsAnswersArray
                                  questionIndex=""
                                  controlName="qAsTreatments"
                                  questionsName="qAsNonCompTreatments"
                                  activator={
                                    blockControl.controls?.qAsComparative
                                      ?.value === "non-comparative"
                                  }
                                  formGroup={blockControl}
                                  inputTextBoxes={[["", "valueHelper", "text"]]}
                                  required={true}
                                  onBlur={validateOnBlur}
                                  onChange={scheduleValidation}
                                  error={
                                    treatmentsErrorBlocks.includes(blockIndex)
                                      ? DUPLICATE_TREATMENTS_MSG
                                      : emptyQuotesTreatmentsBlocks.includes(
                                            blockIndex
                                          )
                                        ? EMPTY_QUOTES_MSG
                                        : missingTreatmentsBlocks.includes(
                                              blockIndex
                                            )
                                          ? "At least one treatment group is required."
                                          : null
                                  }
                                />
                                <QuestionsAnswersArray
                                  questionIndex=""
                                  controlName="qAsAnalytes"
                                  questionsName="qAsAnalytes"
                                  activator={
                                    blockControl.controls?.qAsComparative
                                      ?.value === "comparative" ||
                                    blockControl.controls?.qAsComparative
                                      ?.value === "non-comparative"
                                  }
                                  formGroup={blockControl}
                                  inputTextBoxes={[["", "valueHelper", "text"]]}
                                  placeholder={PLACEHOLDER_ANALYTES}
                                  onBlur={validateOnBlur}
                                  onChange={scheduleValidation}
                                  required={
                                    blockControl.controls?.qAsComparative
                                      ?.value === "comparative"
                                  }
                                  error={
                                    analytesErrorBlocks.includes(blockIndex)
                                      ? DUPLICATE_ANALYTES_MSG
                                      : emptyQuotesAnalytesBlocks.includes(
                                            blockIndex
                                          )
                                        ? EMPTY_QUOTES_MSG
                                        : missingAnalytesBlocks.includes(
                                              blockIndex
                                            )
                                          ? "This field is required for Comparative analysis."
                                          : null
                                  }
                                />
                                <QuestionsAnswersArray
                                  questionIndex=""
                                  controlName="qAsTimepoints"
                                  questionsName="qAsTimepoints"
                                  activator={
                                    blockControl.controls?.qAsComparative
                                      ?.value === "comparative" ||
                                    blockControl.controls?.qAsComparative
                                      ?.value === "non-comparative"
                                  }
                                  formGroup={blockControl}
                                  inputTextBoxes={[["", "valueHelper", "text"]]}
                                  placeholder={PLACEHOLDER_TIMEPOINTS}
                                  onBlur={validateOnBlur}
                                  onChange={scheduleValidation}
                                  error={
                                    timepointsErrorBlocks.includes(blockIndex)
                                      ? DUPLICATE_TIMEPOINTS_MSG
                                      : emptyQuotesTimepointsBlocks.includes(
                                            blockIndex
                                          )
                                        ? EMPTY_QUOTES_MSG
                                        : null
                                  }
                                />
                              </>
                            )}
                          />
                          <p className="small text-muted mt-2">
                            (*) field is required
                          </p>
                        </div>
                      ))}
                    {duplicateWarning && (
                      <div className="alert alert-warning mb-3" role="alert">
                        {duplicateWarning}
                      </div>
                    )}
                    {controls &&
                      Array.isArray(controls) &&
                      controls.length < MAX_BLOCKS && (
                        <div className="add-analysis-block">
                          <button
                            type="button"
                            className="add-analysis-block__btn"
                            onClick={addBlock}
                            aria-label="Add analysis block">
                            <PlusIcon />
                          </button>
                          <span className="add-analysis-block__label">
                            Add analysis block
                          </span>
                          <span className="add-analysis-block__hint">
                            (Max. {MAX_BLOCKS} blocks per table.)
                          </span>
                        </div>
                      )}
                  </>
                )}
              />
            )}
          />
        </form>
      </div>
      <ToastContainer
        position="bottom-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle=""
      headerButton={false}
      ignoreSaveErrors={true}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "TriloAI Dashboard",
        buttonGoToAction: "/csr/" + studyId + "/advancedsearch",
      }}
      nextButton={{
        buttonTitle: "Generate",
        buttonGoToAction:
          "/csr/" + studyId + "/table-analysis-ai-response/" + tableId,
        disabled:
          !!duplicateWarning ||
          timepointsErrorBlocks.length > 0 ||
          analytesErrorBlocks.length > 0 ||
          treatmentsErrorBlocks.length > 0 ||
          missingAnalytesBlocks.length > 0 ||
          missingTreatmentsBlocks.length > 0 ||
          emptyQuotesTreatmentsBlocks.length > 0 ||
          emptyQuotesAnalytesBlocks.length > 0 ||
          emptyQuotesTimepointsBlocks.length > 0,
      }}
      saveButtonHandler={handleSave}
      saveButton={false}
      saveButtonDisabled={
        submitting ||
        !!duplicateWarning ||
        timepointsErrorBlocks.length > 0 ||
        analytesErrorBlocks.length > 0 ||
        treatmentsErrorBlocks.length > 0 ||
        missingAnalytesBlocks.length > 0 ||
        missingTreatmentsBlocks.length > 0 ||
        emptyQuotesTreatmentsBlocks.length > 0 ||
        emptyQuotesAnalytesBlocks.length > 0 ||
        emptyQuotesTimepointsBlocks.length > 0
      }
    />
  );
};

export default TableAnalysisQuestions;
